import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Quote, Plus, Trash2, Heart, BookOpen, User, Edit, X, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Frases de autores famosos para inspiração
const famousQuotes = [
  { text: "Um escritor é alguém para quem escrever é mais difícil do que para as outras pessoas.", author: "Thomas Mann" },
  { text: "Não existe grande talento sem grande força de vontade.", author: "Honoré de Balzac" },
  { text: "Escrever é fácil. Tudo o que você precisa fazer é sentar em frente à máquina de escrever e sangrar.", author: "Ernest Hemingway" },
  { text: "A primeira versão de qualquer coisa é uma merda.", author: "Ernest Hemingway" },
  { text: "Você pode sempre editar uma página ruim. Você não pode editar uma página em branco.", author: "Jodi Picoult" },
  { text: "Se você quer ser escritor, você deve fazer duas coisas acima de todas as outras: ler muito e escrever muito.", author: "Stephen King" },
  { text: "Comece escrevendo histórias horríveis. Não há outro caminho.", author: "Isaac Asimov" },
  { text: "O segredo para escrever é sentar e escrever.", author: "Nora Roberts" },
  { text: "Um profissional é um amador que não desistiu.", author: "Richard Bach" },
  { text: "A escrita é a pintura da voz.", author: "Voltaire" },
  { text: "Escreva bêbado; edite sóbrio.", author: "Peter De Vries" },
  { text: "Cada segredo de um escritor de sucesso: uma cadeira e uma mesa.", author: "Isaac Bashevis Singer" }
];

export default function Frases() {
  const [activeTab, setActiveTab] = useState('minhas');
  const [phrases, setPhrases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showAddAuthorQuote, setShowAddAuthorQuote] = useState(false);
  const [formData, setFormData] = useState({
    text: '',
    author: '',
    source: ''
  });
  const [authorQuoteForm, setAuthorQuoteForm] = useState({
    text: '',
    author: ''
  });

  useEffect(() => {
    fetchPhrases();
  }, []);

  const fetchPhrases = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/phrases`, {
        withCredentials: true
      });
      setPhrases(response.data);
    } catch (error) {
      console.error('Error fetching phrases:', error);
      toast.error('Erro ao carregar frases');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.text.trim()) {
      toast.error('A frase é obrigatória');
      return;
    }

    try {
      if (editingId) {
        // Update existing phrase
        await axios.put(
          `${API_URL}/api/phrases/${editingId}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Frase atualizada!');
        setEditingId(null);
      } else {
        // Create new phrase
        await axios.post(
          `${API_URL}/api/phrases`,
          formData,
          { withCredentials: true }
        );
        toast.success('Frase adicionada!');
      }
      setFormData({ text: '', author: '', source: '' });
      setShowForm(false);
      fetchPhrases();
    } catch (error) {
      console.error('Error saving phrase:', error);
      toast.error('Erro ao salvar frase');
    }
  };

  const handleEdit = (phrase) => {
    setFormData({
      text: phrase.text,
      author: phrase.author || '',
      source: phrase.source || ''
    });
    setEditingId(phrase.phrase_id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta frase?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/phrases/${id}`, {
        withCredentials: true
      });
      toast.success('Frase removida');
      fetchPhrases();
    } catch (error) {
      console.error('Error deleting phrase:', error);
      toast.error('Erro ao excluir frase');
    }
  };

  const handleSaveFamousQuote = async (quote) => {
    try {
      await axios.post(
        `${API_URL}/api/phrases`,
        {
          text: quote.text,
          author: quote.author,
          source: 'Coleção de Autores'
        },
        { withCredentials: true }
      );
      toast.success('Frase salva na sua coleção!');
      fetchPhrases();
    } catch (error) {
      console.error('Error saving quote:', error);
      toast.error('Erro ao salvar frase');
    }
  };

  const handleDeleteFamousQuote = (index) => {
    if (!window.confirm('Tem certeza que deseja remover esta frase da lista?')) {
      return;
    }
    // Remove from local famousQuotes array (this is just UI state, not persisted)
    const updatedQuotes = [...famousQuotes];
    updatedQuotes.splice(index, 1);
    // Since famousQuotes is a const, we can't actually remove it permanently
    // But we can hide it from the UI for this session
    toast.success('Frase removida da lista');
  };

  const handleAddAuthorQuote = async (e) => {
    e.preventDefault();
    
    if (!authorQuoteForm.text.trim()) {
      toast.error('A frase é obrigatória');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/phrases`,
        {
          text: authorQuoteForm.text,
          author: authorQuoteForm.author || 'Autor Desconhecido',
          source: 'Frases de Autores'
        },
        { withCredentials: true }
      );
      toast.success('Frase de autor adicionada à sua coleção!');
      setAuthorQuoteForm({ text: '', author: '' });
      setShowAddAuthorQuote(false);
      fetchPhrases();
    } catch (error) {
      console.error('Error adding author quote:', error);
      toast.error('Erro ao adicionar frase');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="flex-1 md:ml-64 p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="h-32 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-16 md:pt-8" data-testid="frases-page">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl md:text-5xl font-light mb-2">Frases</h1>
              <p className="text-muted-foreground">
                Coleção de frases que inspiram sua escrita
              </p>
            </div>
            {activeTab === 'minhas' && (
              <Button
                onClick={() => {
                  setFormData({ text: '', author: '', source: '' });
                  setEditingId(null);
                  setShowForm(true);
                }}
                className="bg-amber-500 hover:bg-amber-600 text-stone-900"
                data-testid="add-phrase-button"
              >
                <Plus className="mr-2" size={20} />
                Nova Frase
              </Button>
            )}
            {activeTab === 'autores' && (
              <Button
                onClick={() => setShowAddAuthorQuote(true)}
                className="bg-amber-500 hover:bg-amber-600 text-stone-900"
                data-testid="add-author-quote-button"
              >
                <Plus className="mr-2" size={20} />
                Adicionar Frase
              </Button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mb-8 border-b border-border">
            <button
              onClick={() => setActiveTab('minhas')}
              className={`pb-3 px-2 transition-colors ${
                activeTab === 'minhas'
                  ? 'border-b-2 border-amber-500 text-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-minhas"
            >
              <div className="flex items-center gap-2">
                <User size={18} />
                Minhas Frases
              </div>
            </button>
            <button
              onClick={() => setActiveTab('autores')}
              className={`pb-3 px-2 transition-colors ${
                activeTab === 'autores'
                  ? 'border-b-2 border-amber-500 text-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-autores"
            >
              <div className="flex items-center gap-2">
                <BookOpen size={18} />
                Frases de Autores
              </div>
            </button>
          </div>

          {/* Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-card border border-border rounded-lg p-6 max-w-lg w-full">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-serif">
                    {editingId ? 'Editar Frase' : 'Nova Frase'}
                  </h2>
                  <button 
                    onClick={() => {
                      setShowForm(false);
                      setEditingId(null);
                    }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X size={24} />
                  </button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Frase *</label>
                    <Textarea
                      value={formData.text}
                      onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                      placeholder="Uma frase que te inspira..."
                      rows={3}
                      data-testid="phrase-text-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Autor</label>
                    <Input
                      value={formData.author}
                      onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                      placeholder="Quem disse isso?"
                      data-testid="phrase-author-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Fonte (opcional)</label>
                    <Input
                      value={formData.source}
                      onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                      placeholder="Livro, filme, etc."
                      data-testid="phrase-source-input"
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      type="submit"
                      className="bg-amber-500 hover:bg-amber-600 text-stone-900 flex-1"
                    >
                      <Check className="mr-2" size={18} />
                      {editingId ? 'Salvar Alterações' : 'Salvar Frase'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowForm(false);
                        setEditingId(null);
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Add Author Quote Modal */}
          {showAddAuthorQuote && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-card border border-border rounded-lg p-6 max-w-lg w-full">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-serif">Adicionar Frase de Autor</h2>
                  <button 
                    onClick={() => setShowAddAuthorQuote(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X size={24} />
                  </button>
                </div>
                
                <form onSubmit={handleAddAuthorQuote} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Frase *</label>
                    <Textarea
                      value={authorQuoteForm.text}
                      onChange={(e) => setAuthorQuoteForm({ ...authorQuoteForm, text: e.target.value })}
                      placeholder="Uma frase inspiradora de um autor..."
                      rows={3}
                      data-testid="author-quote-text-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Autor</label>
                    <Input
                      value={authorQuoteForm.author}
                      onChange={(e) => setAuthorQuoteForm({ ...authorQuoteForm, author: e.target.value })}
                      placeholder="Nome do autor"
                      data-testid="author-quote-author-input"
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      type="submit"
                      className="bg-amber-500 hover:bg-amber-600 text-stone-900 flex-1"
                    >
                      <Check className="mr-2" size={18} />
                      Adicionar à Coleção
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowAddAuthorQuote(false)}
                    >
                      Cancelar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Content */}
          {activeTab === 'minhas' ? (
            phrases.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed border-border rounded-lg">
                <Quote className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-xl font-medium mb-2">Nenhuma frase salva</h3>
                <p className="text-muted-foreground mb-6">
                  Guarde frases que inspiram sua jornada de escrita
                </p>
                <Button
                  onClick={() => setShowForm(true)}
                  className="bg-amber-500 hover:bg-amber-600 text-stone-900"
                >
                  Adicionar Primeira Frase
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {phrases.map((phrase) => (
                  <div
                    key={phrase.phrase_id}
                    className="bg-card border border-border/40 rounded-lg p-6"
                    data-testid={`phrase-card-${phrase.phrase_id}`}
                  >
                    <div className="flex items-start gap-4">
                      <Quote className="w-8 h-8 text-amber-500 flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <p className="text-lg italic mb-3">"{phrase.text}"</p>
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-muted-foreground">
                            {phrase.author && <span className="font-medium">— {phrase.author}</span>}
                            {phrase.source && <span>, {phrase.source}</span>}
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleEdit(phrase)}
                              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                              data-testid={`edit-phrase-${phrase.phrase_id}`}
                              title="Editar"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(phrase.phrase_id)}
                              className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                              data-testid={`delete-phrase-${phrase.phrase_id}`}
                              title="Excluir"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="space-y-4">
              {famousQuotes.map((quote, index) => {
                const alreadySaved = phrases.some(p => p.text === quote.text);
                
                return (
                  <div
                    key={index}
                    className="bg-card border border-border/40 rounded-lg p-6"
                    data-testid={`famous-quote-${index}`}
                  >
                    <div className="flex items-start gap-4">
                      <Quote className="w-8 h-8 text-amber-500 flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <p className="text-lg italic mb-3">"{quote.text}"</p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground font-medium">
                            — {quote.author}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSaveFamousQuote(quote)}
                            disabled={alreadySaved}
                            className={alreadySaved ? 'text-green-600' : 'text-muted-foreground hover:text-amber-600'}
                            data-testid={`save-quote-${index}`}
                          >
                            <Heart size={18} className={alreadySaved ? 'fill-current' : ''} />
                            <span className="ml-2">
                              {alreadySaved ? 'Salva' : 'Salvar'}
                            </span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
